import { useCompanies } from 'store/company';
import { useProjectUsers } from 'store/projectUsers';
import { toLabelledEntities } from 'helpers/misc';
import { sortLabelledEntities } from 'helpers/sort';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { logChannelRequestFailure } from 'setup/logger/logChannelRequestFailure';
import { analyticsClient } from 'shared/domain/analytics/analyticsClient';
import {
  AnalyticsChart,
  ChartAdditionalParams,
  ChartRequest,
  ChartSeries,
  hasUnresolvedChartParams,
} from 'shared/domain/analytics/chartModel';
import { createLatestRead } from 'store/latestRead';
import { useProjectOrganizationId } from 'store/project';
import { isSuperAdmin } from 'shared/domain/role/isSuperAdmin';
import { userModelToLabelledWithEmail } from 'shared/domain/user/mapping/toView';
import { UserModel } from 'shared/domain/user/types/model';
import { AnyFilters, ChartValuesAndLabels } from 'shared/types/analytics';
import { LabelledEntity } from 'shared/types/commonView';
import { extractFiltersValues } from './filterValues';
import { GetChart } from './types';
import { isStandard } from 'shared/domain/role/isStandard';
import { isViewer } from 'shared/domain/role/isViewer';
import { useImpacts } from 'store/fieldValue';

function toAssignees(
  users: UserModel[],
  organizationId: string
): LabelledEntity[] {
  return Object.values(users)
    .filter((user) => !isSuperAdmin(user.role))
    .map((user) => userModelToLabelledWithEmail(user, organizationId))
    .sort(sortLabelledEntities);
}

function toAtLeastManagers(
  users: UserModel[],
  organizationId: string
): LabelledEntity[] {
  return Object.values(users)
    .filter(
      (user) =>
        !isSuperAdmin(user.role) &&
        !isStandard(user.role) &&
        !isViewer(user.role)
    )
    .map((user) => userModelToLabelledWithEmail(user, organizationId))
    .sort(sortLabelledEntities);
}

export function useSubcontractors(): LabelledEntity[] {
  const {
    items: companies,
  } = useCompanies();

  const subcontractors = useMemo(
    () =>
      companies
        .map(({ _id, shortLabel }) => ({
          _id,
          label: shortLabel,
        }))
        .sort(sortLabelledEntities),
    [companies]
  );

  return subcontractors;
}

export function useAssignees(): LabelledEntity[] {
  const { items: users } = useProjectUsers();
  const organizationId = useProjectOrganizationId();
  return useMemo(
    () => toAssignees(users, organizationId),
    [users, organizationId]
  );
}

export function useImpactFilterOptions(): LabelledEntity[] {
  const { allItems } = useImpacts();

  return useMemo(
    () => toLabelledEntities(allItems).sort(sortLabelledEntities),
    [allItems]
  );
}

export function useAtLeastManagers(): LabelledEntity[] {
  const { items: users } = useProjectUsers();
  const organizationId = useProjectOrganizationId();
  return useMemo(
    () => toAtLeastManagers(users, organizationId),
    [users, organizationId]
  );
}

export const EMPTY_VALUES_AND_LABELS: ChartValuesAndLabels = [[], []];

interface UseGetChartParams<TChart extends AnalyticsChart> {
  chart: TChart;
  /** The chart waits while its filters are not built yet. */
  filters: AnyFilters | undefined;
  additionalParams?: ChartAdditionalParams;
  /** Shown until the first answer. */
  initialSeries: ChartSeries<TChart>;
}

interface SignedChartRequest<TChart extends AnalyticsChart> {
  request: ChartRequest<TChart>;
  /** Equal for requests that would get the same chart. */
  signature: string;
}

function getChartRequestSignature({ chart, locale, filters, additionalParams }: ChartRequest): string {
  return JSON.stringify({ chart, locale, filters, additionalParams: additionalParams ?? null });
}

/**
 * The series of a chart, read again when its filters, params or the locale
 * change; `reload` asks to calculate it again. Only the latest request applies.
 */
export function useGetChart<TChart extends AnalyticsChart>({
  chart,
  filters,
  additionalParams,
  initialSeries,
}: UseGetChartParams<TChart>): GetChart<ChartSeries<TChart>> {
  const { locale } = useIntl();
  const [loading, setLoading] = useState(true);
  const [series, setSeries] = useState<ChartSeries<TChart>>(initialSeries);
  const [updatedAt, setUpdatedAt] = useState(0);
  const latestRead = useMemo(() => createLatestRead(), []);
  const requestedSignatureRef = useRef<string | null>(null);

  const signedRequest = useMemo<SignedChartRequest<TChart> | undefined>(() => {
    if (!filters || hasUnresolvedChartParams(additionalParams)) return undefined;
    const request = { chart, filters: extractFiltersValues(filters), locale, additionalParams };
    return { request, signature: getChartRequestSignature(request) };
  }, [chart, filters, locale, additionalParams]);
  const signedRequestRef = useRef(signedRequest);
  signedRequestRef.current = signedRequest;

  const load = useCallback(
    (forceUpdate = false) => {
      if (!signedRequestRef.current) return;

      const { request: currentRequest, signature } = signedRequestRef.current;
      if (!forceUpdate && requestedSignatureRef.current === signature) return;
      requestedSignatureRef.current = signature;

      setLoading(true);
      void latestRead.run(() => analyticsClient.getChart({ ...currentRequest, forceUpdate }), {
        onResult: (result) => {
          setSeries(result.series);
          setUpdatedAt(result.updatedAt);
          setLoading(false);
        },
        onError: (error) => {
          logChannelRequestFailure('Could not load chart', error, { chart: currentRequest.chart });
          requestedSignatureRef.current = null;
          setLoading(false);
        },
      });
    },
    [latestRead]
  );

  const reload = useCallback(() => load(true), [load]);

  const requestSignature = signedRequest?.signature;
  useEffect(() => {
    if (requestSignature) {
      load();
      return;
    }
    // Filters or params gone: the answer of a request in flight no longer applies.
    latestRead.cancel();
    requestedSignatureRef.current = null;
  }, [requestSignature, load, latestRead]);

  useEffect(() => latestRead.cancel, [latestRead]);

  // Filters gone (another project is being read): its data must not pass for the new project's while loading.
  useEffect(() => {
    if (!filters) setUpdatedAt(0);
  }, [filters]);

  return { series, loading, load, reload, updatedAt };
}
